import {
  DEFAULT_GLOBE_VIEWPORT,
  AIRCRAFT_STALE_AFTER_SECONDS,
  CYBER_SOURCE_IDS,
  DEFAULT_RENDERER_MODE,
  ENERGY_INFRASTRUCTURE_SOURCE_IDS,
  GLOBE_MAX_ALTITUDE,
  GFW_FIXED_INFRASTRUCTURE_SOURCE_ID,
  GDELT_CONFLICT_EVENTS_SOURCE_ID,
  GLOBE_MIN_ALTITUDE,
  KEYLESS_EVENT_SOURCE_IDS,
  KEYLESS_INFRASTRUCTURE_SOURCE_IDS,
  KEYLESS_LIVE_SOURCE_IDS,
  MAP_ONLY_TIME_RANGE_SOURCE_IDS,
  OPENSKY_AIRCRAFT_SOURCE_ID,
  OVERLAY_DATE_MODES,
  RENDERER_MODES,
  RETIRED_MAP_CONTROLS,
  SATELLITE_STALE_AFTER_SECONDS,
  SEVERITIES,
  SOURCE_LIMITS,
  SOURCE_SETTING_DEFINITIONS,
  SPACE_SATELLITES_SOURCE_ID,
  SPACE_WEATHER_SOURCE_ID,
  TIME_RANGES,
  TRANSIT_INFRASTRUCTURE_SOURCE_IDS,
  TRANSITLAND_DEPARTURES_SOURCE_ID,
  TRANSITLAND_SERVICE_ALERTS_SOURCE_ID,
} from "./constants";
import { inSceneRange, sceneRange } from "../../../Universal/lens/rangeFilter";

export const getEventId = (event) => event?.properties?.id || event?.id;

export const isClusterEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return properties.cluster === true || properties.kind === "cluster";
};

export const formatTimestamp = (timestamp) => {
  if (!timestamp) return "No timestamp";
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(timestamp * 1000));
  } catch {
    return "No timestamp";
  }
};

export const displayText = (value, fallback = "") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    return value.map((item) => displayText(item)).filter(Boolean).join(", ") || fallback;
  }
  if (typeof value === "object") {
    return (
      value.name ||
      value.label ||
      value.title ||
      value.code ||
      value.id ||
      fallback
    ).toString();
  }
  return String(value);
};

export const sortByTimeAscending = (events) =>
  [...events].sort(
    (a, b) =>
      (a.properties?.timestamp || Number.MAX_SAFE_INTEGER) -
      (b.properties?.timestamp || Number.MAX_SAFE_INTEGER),
  );

const validLatLng = (lat, lng) =>
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180;

export const getEventLatLng = (event) => {
  const coords = event?.geometry?.coordinates || [];
  if (event?.geometry?.type !== "Point" || coords.length < 2) return null;
  const lon = Number(coords[0]);
  const lat = Number(coords[1]);
  if (!validLatLng(lat, lon)) return null;
  return [lat, lon];
};

const linePathToLatLngs = (path) => {
  if (!Array.isArray(path)) return [];
  return path
    .map((point) => {
      if (!Array.isArray(point) || point.length < 2) return null;
      const lng = Number(point[0]);
      const lat = Number(point[1]);
      return validLatLng(lat, lng) ? [lat, lng] : null;
    })
    .filter(Boolean);
};

export const getEventLineLatLngs = (event) => {
  const geometry = event?.geometry || {};
  const coords = geometry.coordinates;
  if (geometry.type === "LineString") {
    const path = linePathToLatLngs(coords);
    return path.length >= 2 ? [path] : [];
  }
  if (geometry.type === "MultiLineString" && Array.isArray(coords)) {
    return coords
      .map(linePathToLatLngs)
      .filter((path) => path.length >= 2);
  }
  return [];
};

export const isLineEvent = (event) => getEventLineLatLngs(event).length > 0;

export const getEventAnchorLatLng = (event) => {
  const point = getEventLatLng(event);
  if (point) return point;
  const paths = getEventLineLatLngs(event);
  const points = paths.flat();
  if (!points.length) return null;
  const middle = points[Math.floor(points.length / 2)];
  return middle || null;
};

export const isAircraftEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return properties.source === OPENSKY_AIRCRAFT_SOURCE_ID;
};

export const isSatelliteEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return properties.source === SPACE_SATELLITES_SOURCE_ID;
};

export const isSpaceWeatherEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return properties.source === SPACE_WEATHER_SOURCE_ID;
};

export const isEnergyInfrastructureEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return ENERGY_INFRASTRUCTURE_SOURCE_IDS.includes(properties.source);
};

export const isTransitInfrastructureEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return TRANSIT_INFRASTRUCTURE_SOURCE_IDS.includes(properties.source);
};

export const isTransitDepartureEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return properties.source === TRANSITLAND_DEPARTURES_SOURCE_ID;
};

export const isTransitServiceAlertEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return properties.source === TRANSITLAND_SERVICE_ALERTS_SOURCE_ID;
};

export const isTransitEvent = (eventOrProperties) =>
  isTransitInfrastructureEvent(eventOrProperties) ||
  isTransitDepartureEvent(eventOrProperties) ||
  isTransitServiceAlertEvent(eventOrProperties);

export const isGfwInfrastructureEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return properties.source === GFW_FIXED_INFRASTRUCTURE_SOURCE_ID;
};

export const isGfwEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return String(properties.source || "").startsWith("gfw_") || Boolean(properties.meta?.gfwVesselId || properties.meta?.vesselId);
};

export const isCyberEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return CYBER_SOURCE_IDS.includes(properties.source) || String(properties.category || "").toLowerCase().startsWith("cyber");
};

export const isGdeltEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return properties.source === GDELT_CONFLICT_EVENTS_SOURCE_ID;
};

export const isKeylessEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return KEYLESS_EVENT_SOURCE_IDS.includes(properties.source);
};

export const isKeylessLiveEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return KEYLESS_LIVE_SOURCE_IDS.includes(properties.source);
};

export const isKeylessInfrastructureEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return KEYLESS_INFRASTRUCTURE_SOURCE_IDS.includes(properties.source);
};

export const isOsmInfrastructureEvent = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return String(properties.source || "").startsWith("osm_");
};

export const getGfwVesselId = (eventOrProperties) => {
  const properties = eventOrProperties?.properties || eventOrProperties || {};
  return displayText(properties.meta?.gfwVesselId || properties.meta?.vesselId || properties.meta?.vessel?.id, "");
};

export const getAircraftId = (event) => {
  const meta = event?.properties?.meta || {};
  return displayText(meta.icao24 || getEventId(event), "");
};

export const getAircraftHeading = (event) => {
  const heading = Number(event?.properties?.meta?.trueTrackDeg);
  if (!Number.isFinite(heading)) return 0;
  return ((heading % 360) + 360) % 360;
};

const getFreshness = (event, staleAfterSeconds, nowMs = Date.now()) => {
  const timestamp = Number(event?.properties?.timestamp);
  if (!Number.isFinite(timestamp)) {
    return { ageSeconds: null, isStale: false, label: "freshness unknown" };
  }

  const ageSeconds = Math.max(0, Math.floor(nowMs / 1000 - timestamp));
  const isStale = ageSeconds > staleAfterSeconds;
  const label = ageSeconds < 60
    ? `${ageSeconds}s ago`
    : `${Math.floor(ageSeconds / 60)}m ago`;
  return { ageSeconds, isStale, label };
};

export const getAircraftFreshness = (event, nowMs = Date.now()) =>
  getFreshness(event, AIRCRAFT_STALE_AFTER_SECONDS, nowMs);

export const getSatelliteId = (event) => {
  const meta = event?.properties?.meta || {};
  return displayText(meta.noradCatId || getEventId(event), "");
};

export const getSatelliteFreshness = (event, nowMs = Date.now()) =>
  getFreshness(event, SATELLITE_STALE_AFTER_SECONDS, nowMs);

const formatNumber = (value, options = {}) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "Unknown";
  return new Intl.NumberFormat(undefined, options).format(numeric);
};

export const formatAircraftAltitude = (meters) => {
  const numeric = Number(meters);
  if (!Number.isFinite(numeric)) return "Unknown";
  return `${formatNumber(numeric * 3.28084, { maximumFractionDigits: 0 })} ft`;
};

export const formatAircraftSpeed = (metersPerSecond) => {
  const numeric = Number(metersPerSecond);
  if (!Number.isFinite(numeric)) return "Unknown";
  return `${formatNumber(numeric * 1.94384, { maximumFractionDigits: 0 })} kt`;
};

export const formatAircraftVerticalRate = (metersPerSecond) => {
  const numeric = Number(metersPerSecond);
  if (!Number.isFinite(numeric)) return "Unknown";
  return `${numeric >= 0 ? "+" : ""}${formatNumber(numeric * 196.85, { maximumFractionDigits: 0 })} ft/min`;
};

export const formatAircraftHeading = (degrees) => {
  const numeric = Number(degrees);
  if (!Number.isFinite(numeric)) return "Unknown";
  return `${Math.round(((numeric % 360) + 360) % 360)}°`;
};

export const getPlaybackMarkerState = ({
  currentPlaybackEventId,
  event,
  playbackActive,
  playheadTimestamp,
}) => {
  if (!playbackActive) return "idle";
  const eventId = getEventId(event);
  if (currentPlaybackEventId && eventId === currentPlaybackEventId) return "current";
  // §48.2 C — no playhead means no past/future ladder (the caller already sliced
  // its set), but "current" above still stands: it is an identity, not a time
  // comparison.
  if (typeof playheadTimestamp !== "number") return "idle";
  const timestamp = event?.properties?.timestamp;
  if (typeof timestamp !== "number") return "past";
  return timestamp > playheadTimestamp ? "future" : "past";
};

export const rangeStartTimestamp = (rangeKey) => {
  const range = TIME_RANGES.find((item) => item.key === rangeKey);
  if (!range?.seconds) return null;
  return Math.floor(Date.now() / 1000) - range.seconds;
};

export const filterEventsByTimeRange = (events, rangeKey) => {
  const cutoff = rangeStartTimestamp(rangeKey);
  if (!cutoff) return events;
  return events.filter((event) => {
    const properties = event.properties || {};
    const timestamp = properties.timestamp;
    if (isClusterEvent(event)) {
      const clusterSourceIds = Array.isArray(properties.sourceIds)
        ? properties.sourceIds
        : [properties.source].filter(Boolean);
      if (clusterSourceIds.length && clusterSourceIds.every((sourceId) => MAP_ONLY_TIME_RANGE_SOURCE_IDS.includes(sourceId))) {
        return true;
      }
      return typeof timestamp === "number" ? timestamp >= cutoff : true;
    }
    if (MAP_ONLY_TIME_RANGE_SOURCE_IDS.includes(properties.source)) return true;
    return typeof timestamp === "number" && timestamp >= cutoff;
  });
};

// §48.1 — the brush IS the visible slice, applied to the plotted features.
// A map feature is a POINT IN TIME (`properties.timestamp`), so intersection
// collapses to containment: from ≤ timestamp ≤ to. Timeless features are kept
// on exactly the same rule filterEventsByTimeRange already uses — no
// timestamp, or a map-only source (infrastructure: airports, borders, cables)
// whose presence is not an event.
export const filterEventsByBrush = (events, brush, playheadTimestamp = null) => {
  const range = sceneRange(brush, playheadTimestamp);
  if (!range || !Array.isArray(events)) return events;
  return events.filter((event) => {
    const properties = event?.properties || {};
    const timestamp = properties.timestamp;
    if (typeof timestamp !== "number") return true;
    if (isClusterEvent(event)) {
      const clusterSourceIds = Array.isArray(properties.sourceIds)
        ? properties.sourceIds
        : [properties.source].filter(Boolean);
      if (
        clusterSourceIds.length &&
        clusterSourceIds.every((sourceId) =>
          MAP_ONLY_TIME_RANGE_SOURCE_IDS.includes(sourceId),
        )
      ) {
        return true;
      }
    } else if (MAP_ONLY_TIME_RANGE_SOURCE_IDS.includes(properties.source)) {
      return true;
    }
    return inSceneRange(range, timestamp, timestamp);
  });
};

export const normalizeViewport = (viewport) => {
  if (!viewport?.bounds) return null;
  const round = (value) => Number(Number(value).toFixed(4));
  const zoom = Number(viewport.zoom);
  const bounds = {
    south: round(viewport.bounds.south),
    west: round(viewport.bounds.west),
    north: round(viewport.bounds.north),
    east: round(viewport.bounds.east),
  };
  if (
    !Number.isFinite(zoom) ||
    !Object.values(bounds).every((value) => Number.isFinite(value))
  ) {
    return null;
  }
  return {
    bounds,
    zoom,
  };
};

const roundCoordinate = (value, precision = 4) => Number(Number(value).toFixed(precision));
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export const normalizeRendererMode = (mode) =>
  Object.values(RENDERER_MODES).includes(mode) ? mode : DEFAULT_RENDERER_MODE;

export const normalizeGlobeViewport = (viewport) => {
  if (!viewport || typeof viewport !== "object") return null;
  const lat = Number(viewport.lat);
  const lng = Number(viewport.lng);
  const altitude = Number(viewport.altitude);
  if (![lat, lng, altitude].every(Number.isFinite)) return null;
  return {
    lat: roundCoordinate(clamp(lat, -85, 85)),
    lng: roundCoordinate((((lng + 180) % 360) + 360) % 360 - 180),
    altitude: Number(clamp(altitude, GLOBE_MIN_ALTITUDE, GLOBE_MAX_ALTITUDE).toFixed(3)),
  };
};

export const globeViewportsEqual = (a, b) =>
  Boolean(a && b) &&
  a.lat === b.lat &&
  a.lng === b.lng &&
  a.altitude === b.altitude;

export const globeAltitudeFromZoom = (zoom) => {
  const numericZoom = Number(zoom);
  if (!Number.isFinite(numericZoom)) return DEFAULT_GLOBE_VIEWPORT.altitude;
  const altitude = 2.7 / 2 ** ((numericZoom - 2) / 3.2);
  return Number(clamp(altitude, GLOBE_MIN_ALTITUDE, GLOBE_MAX_ALTITUDE).toFixed(3));
};

export const globeZoomFromAltitude = (altitude) => {
  const numericAltitude = Number(altitude);
  if (!Number.isFinite(numericAltitude) || numericAltitude <= 0) return 2;
  const zoom = 2 + Math.log2(2.7 / numericAltitude) * 3.2;
  return Math.round(clamp(zoom, 2, 19));
};

export const globeViewportFromFlatViewport = (viewport) => {
  const flatViewport = normalizeViewport(viewport);
  if (!flatViewport?.bounds) return null;
  const { bounds } = flatViewport;
  return normalizeGlobeViewport({
    lat: (bounds.south + bounds.north) / 2,
    lng: (bounds.west + bounds.east) / 2,
    altitude: globeAltitudeFromZoom(flatViewport.zoom),
  });
};

export const normalizeRendererViewports = (viewports, fallbackFlatViewport = null) => {
  const safeViewports = viewports && typeof viewports === "object" ? viewports : {};
  return {
    flat: normalizeViewport(safeViewports.flat) || fallbackFlatViewport || null,
    globe: normalizeGlobeViewport(safeViewports.globe) || DEFAULT_GLOBE_VIEWPORT,
  };
};

export const viewportsEqual = (a, b) =>
  Boolean(a && b) &&
  a.zoom === b.zoom &&
  a.bounds.south === b.bounds.south &&
  a.bounds.west === b.bounds.west &&
  a.bounds.north === b.bounds.north &&
  a.bounds.east === b.bounds.east;

export const defaultSelectedSourceIds = (sources) =>
  sources
    .filter((source) => source.enabled !== false && source.defaultSelected !== false)
    .map((source) => source.id);

// A retired control (constants.RETIRED_MAP_CONTROLS) is unreachable from the
// bar, so the value it would edit must not survive a restore as an invisible
// filter. Every retired group falls back to NOTHING selected: infrastructure
// short-circuits its viewport fetch, and live feeds stop their polling lanes
// (aircraft, satellites) instead of painting a layer no control can remove.
//
// Live feeds do NOT fall back to the server defaults. The map's events come
// from getMapEvents(requestFeedViews) — the lens's event types — which never
// reads this list; only the legacy getOsintMapEvents lane does, alongside
// signal targets. So an empty live selection cannot empty the map, and
// defaulting it ON forced aircraft and satellites back for everyone who had
// switched them off, with no control left to notice.
//
// The saved value is never rewritten — flipping the flag off restores it as
// the user left it (OsintMapHome carries the raw list through retiredMapStateRef).
const deactivateRetiredSources = (selectedIds, sources) => {
  const { liveFeeds, infrastructure } = RETIRED_MAP_CONTROLS;
  if (!liveFeeds && !infrastructure) return selectedIds;
  const feedTypeById = new Map(
    sources.map((source) => [source.id, source.feedType]),
  );
  const retired = (sourceId) => {
    const feedType = feedTypeById.get(sourceId);
    return (
      (liveFeeds && feedType === "live") ||
      (infrastructure && feedType === "infrastructure")
    );
  };
  return selectedIds.filter((sourceId) => !retired(sourceId));
};

const clampNumber = (value, fallback, min, max) => {
  const numeric = Number(value);
  const safeValue = Number.isFinite(numeric) ? numeric : fallback;
  return Math.max(min, Math.min(max, safeValue));
};

export const normalizeSourceSetting = (sourceId, rawSetting = {}) => {
  const definition = SOURCE_SETTING_DEFINITIONS[sourceId];
  if (!definition) return null;
  const density = ["low", "medium", "high"].includes(rawSetting?.density)
    ? rawSetting.density
    : definition.density;
  const preset = definition.presets?.[density] || {};
  const normalized = { density };

  if (definition.supportsMinZoom && definition.minZoom) {
    normalized.minZoom = Math.round(clampNumber(
      rawSetting?.minZoom ?? preset.minZoom,
      definition.minZoom.default,
      definition.minZoom.min,
      definition.minZoom.max,
    ));
  }
  normalized.maxFeatures = Math.round(clampNumber(
    rawSetting?.maxFeatures ?? preset.maxFeatures,
    definition.maxFeatures.default,
    definition.maxFeatures.min,
    definition.maxFeatures.max,
  ));
  return normalized;
};

export const normalizeSourceSettings = (settings = {}, sources = []) => {
  if (!settings || typeof settings !== "object") return {};
  const sourceIds = new Set(sources.map((source) => source.id));
  return Object.entries(settings).reduce((acc, [sourceId, rawSetting]) => {
    if (!sourceIds.has(sourceId) || !SOURCE_SETTING_DEFINITIONS[sourceId]) return acc;
    const normalized = normalizeSourceSetting(sourceId, rawSetting);
    if (normalized) acc[sourceId] = normalized;
    return acc;
  }, {});
};

export const sourceSettingsToSourceLimits = (sourceSettings = {}) =>
  Object.entries(sourceSettings).reduce((acc, [sourceId, setting]) => {
    if (!SOURCE_SETTING_DEFINITIONS[sourceId]) return acc;
    const normalized = normalizeSourceSetting(sourceId, setting);
    if (normalized?.maxFeatures) acc[sourceId] = normalized.maxFeatures;
    return acc;
  }, { ...SOURCE_LIMITS });

const normalizeOverlayOpacity = (value, fallback = 0.7) => {
  const opacity = Number(value);
  if (!Number.isFinite(opacity)) return fallback;
  return Math.max(0, Math.min(1, opacity));
};

export const normalizeOverlaySettings = (settings, overlays = []) => {
  const overlayById = new Map(overlays.map((overlay) => [overlay.id, overlay]));
  if (!settings || typeof settings !== "object") return {};

  return Object.entries(settings).reduce((acc, [overlayId, rawSetting]) => {
    const overlay = overlayById.get(overlayId);
    if (!overlay || !rawSetting || typeof rawSetting !== "object") return acc;

    const fallbackDateMode = overlay.defaultDateMode || "latest";
    const dateMode = OVERLAY_DATE_MODES.includes(rawSetting.dateMode)
      ? rawSetting.dateMode
      : fallbackDateMode;
    acc[overlayId] = {
      opacity: normalizeOverlayOpacity(rawSetting.opacity, Number(overlay.opacity ?? 0.7)),
      dateMode,
      date: dateMode === "custom" && typeof rawSetting.date === "string"
        ? rawSetting.date.slice(0, 32)
        : null,
    };
    return acc;
  }, {});
};

export const overlayDateValue = (overlay, setting = {}) => {
  const dateMode = setting.dateMode || overlay?.defaultDateMode || "latest";
  if (dateMode === "custom") return setting.date || "latest";
  return dateMode;
};

export const normalizeSavedMapState = (state, sources, overlays = []) => {
  const sourceIds = new Set(sources.map((source) => source.id));
  const overlayIds = new Set(overlays.map((overlay) => overlay.id));
  const timeRangeKeys = new Set(TIME_RANGES.map((range) => range.key));
  return {
    selectedSources: deactivateRetiredSources(
      Array.isArray(state?.selectedSources)
        ? state.selectedSources.filter((sourceId) => sourceIds.has(sourceId))
        : defaultSelectedSourceIds(sources),
      sources,
    ),
    selectedSignalTargets: Array.isArray(state?.selectedSignalTargets)
      ? state.selectedSignalTargets
          .map((target) => String(target || "").trim())
          .filter(Boolean)
      : [],
    selectedSeverities: Array.isArray(state?.selectedSeverities)
      ? state.selectedSeverities.filter((severity) => SEVERITIES.includes(severity))
      : [],
    timeRange: timeRangeKeys.has(state?.timeRange) ? state.timeRange : "24h",
    mapViewport: normalizeViewport(state?.mapViewport) || normalizeViewport(state?.rendererViewports?.flat),
    // §1.1 — no `searchQuery`: the search is ONE shared lens value with no
    // page-local copy, so this doc neither reads nor writes it any more.
    rendererMode: normalizeRendererMode(state?.rendererMode),
    rendererViewports: normalizeRendererViewports(
      state?.rendererViewports,
      normalizeViewport(state?.mapViewport),
    ),
    selectedOverlays: Array.isArray(state?.selectedOverlays)
      ? state.selectedOverlays.filter((overlayId) => overlayIds.has(overlayId))
      : [],
    overlaySettings: normalizeOverlaySettings(state?.overlaySettings, overlays),
    sourceSettings: normalizeSourceSettings(state?.sourceSettings, sources),
  };
};
